import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, RouterStateSnapshot } from '@angular/router';
import { OrganizationType } from '@portal/api/enums/organization-type';
import { Org } from '@portal/api/responses/organizations/org-details-response';
import { AppContextService } from '@portal/shared/services/app-context.service';
import { OrganizationSelectorService } from '@portal/shared/services/organization-selector.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class AppContextOrgTypeGuard implements CanActivate, CanActivateChild {
    private _requestedUrl = '/';

    constructor(
        private readonly _appContextService: AppContextService,
        private readonly _organizationSelectorService: OrganizationSelectorService,
        @Inject(DOCUMENT) private readonly _document: Document,
    ) {
    }

    canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        this._requestedUrl = state.url || '/';
        return this.validateCurrentOrgContext();
    }

    canActivateChild(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        this._requestedUrl = state.url || '/';
        return this.validateCurrentOrgContext();
    }

    private validateCurrentOrgContext(): Observable<boolean> {
        return this._organizationSelectorService.ensureSelectedOrg$().pipe(
            map(org => this.resolveCurrentOrg(org)),
            catchError(() => of(true))
        );
    }

    private resolveCurrentOrg(org: Org | null): boolean {
        const currentOrgType = org?.type;

        if (!this.isContextAwareOrgType(currentOrgType)) {
            return true;
        }

        if (this._appContextService.isOrgTypeAllowed(currentOrgType)) {
            return true;
        }

        const currentContext = this._appContextService.getContext();
        const targetContext = this._appContextService.targetHostForOrgType(currentOrgType);

        if (currentContext === targetContext) {
            return true;
        }

        const redirectUrl = this._appContextService.buildRedirectUrl(
            targetContext,
            new URL(this._requestedUrl, this._document.defaultView?.location.origin ?? 'http://localhost').toString()
        );
        this._document.defaultView?.location.replace(redirectUrl);

        return false;
    }

    private isContextAwareOrgType(type: OrganizationType | string | number | null | undefined): boolean {
        return type === OrganizationType.Party
            || type === OrganizationType.Association
            || type === String(OrganizationType.Party)
            || type === String(OrganizationType.Association)
            || String(type).toLowerCase() === 'party'
            || String(type).toLowerCase() === 'association';
    }
}
